import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMaxDiffCounts,
  fetchMaxDiffEmpiricalBayes,
  fetchMaxDiffTurf,
  fetchMaxDiffProbeQuestions,
} from '../asyncActions/maxdiffAnalysis';

// Every MaxDiff sub-feature (counts, choiceProbability, hb, turf, ...) lives
// as its own sub-key with a { loading, data, error } envelope. New features
// slot in without touching existing keys — reducers stay decoupled from
// each other and selectors stay trivial.
type LoadableState<T> = {
  loading: boolean;
  data: T | null;
  error: string | null;
};

export type CountsItem = {
  item_id: number;
  name: string;
  best_count: number;
  worst_count: number;
  times_shown: number;
  best_proportion: number;
  worst_proportion: number;
  simple_count: number;
  // Legacy pairwise-based adjusted score kept as a debug/audit field.
  // Frontend chart now prefers the *_proportion fields below.
  adjusted_score: number;
  // Tie-break-adjusted proportions — treat each tie-break as an
  // additional best-picking event counted in BOTH numerator and
  // denominator. Bounded 0..1 with the same semantic as
  // `best_proportion` / `worst_proportion`.
  //   adjusted_best_proportion  = (best  + tie_top_wins) / (shown + tie_top_involvement)
  //   adjusted_worst_proportion = (worst + tie_bot_wins) / (shown + tie_bot_involvement)
  adjusted_best_proportion: number;
  adjusted_worst_proportion: number;
  // Per-item tie-break event counts — powering tooltips like
  // "won 4 of 8 top-tie contests".
  tie_top_wins: number;
  tie_top_involvement: number;
  tie_bot_wins: number;
  tie_bot_involvement: number;
};

export type CountsData = {
  items: CountsItem[];
  total_respondents: number;
  // Total directional pairwise votes derived from tie-break resolutions.
  // Zero means the sample had no ties → the "Include tie-break data"
  // toggle is a no-op and the UI hides it.
  tiebreak_votes_total: number;
};

export type CountsViewMode = 'best' | 'worst' | 'bestWorst' | 'simple';

// ── Empirical Bayes ─────────────────────────────────────────────
export type EBTransform = {
  value: number;
  ci_low: number;
  ci_high: number;
  // Tie-adjusted counterparts — same shape, computed by adding each
  // tie-break event to the respondent's own numerator+denominator
  // before running the EB shrinkage & bootstrap. Consumers pick which
  // pair to display via the `ebUseTiebreak` toggle.
  value_tb: number;
  ci_low_tb: number;
  ci_high_tb: number;
};
export type EBItem = {
  item_id: number;
  name: string;
  raw: EBTransform;
  zero_centered: EBTransform;
  probability: EBTransform;
};
export type EBData = {
  items: EBItem[];
  total_respondents: number;
  shrinkage_k: number;
  bootstrap_iters: number;
  // Aggregate tie-break event volume — used to auto-hide the "Include
  // tie-break data" toggle when the sample has zero ties.
  tiebreak_votes_total: number;
};
export type EBViewMode = 'raw' | 'zeroCentered' | 'probability';




// ── TURF ────────────────────────────────────────────────────────
export type TurfCombination = {
  rank: number;
  items: number[];
  item_names: string[];
  reach: number;          // percentage 0..100
  reached_count: number;
  frequency: number;      // 1..combo_size
  combo_size: number;
};
export type TurfData = {
  combinations: TurfCombination[];
  total_respondents: number;
  combo_size: number;
  items_pool_size: number;
  total_combos_evaluated: number;
  appeal_mode: 'best' | 'positive_net';
  top_n: number;
};

type MaxDiffAnalysisState = {
  // A per-item tab is `item:<itemId>` — it renders the standard AI-analysis
  // pane against that item's derived AVT question, so it needs no state of its
  // own beyond the roster resolved below.
  activeTab: string;
  // Item roster + derived question ids, from /analysis/maxdiff/probe_questions/.
  probeQuestions: {
    loading: boolean;
    error: string | null;
    items: Array<{
      itemId: number;
      itemName: string;
      questionId: number | null;
      answerType: string;
    }>;
    // Which MaxDiff question `items` belongs to. The roster is global state
    // but item ids are per question, so a reader must check this matches the
    // question on screen before trusting `items` — otherwise the previous
    // question's items render under the new one while its roster loads.
    forQuestionId: number | null;
    // The latest in-flight request. Switching A -> B quickly leaves A's
    // request outstanding; if it resolves after B's, it must not overwrite
    // B's roster, so responses from any other request are dropped.
    requestId: string | null;
  };
  counts: LoadableState<CountsData>;
  countsView: CountsViewMode;
  countsRender: 'chart' | 'table';
  // "Include tie-break data" toggle. When true, Simple mode's chart
  // values and the ranking sort in the table use `adjusted_score` instead
  // of `simple_count`. Default true so the industry-first feature is
  // visible on landing; analysts who want raw numbers flip it off.
  countsUseTiebreak: boolean;
  eb: LoadableState<EBData>;
  ebView: EBViewMode;
  ebRender: 'chart' | 'table';
  ebSortOrder: 'desc' | 'asc';
  ebShowCi: boolean;
  // "Include tie-break data" toggle for the EB chart. Default true so
  // the tie-adjusted values are the analyst-facing default; flip off
  // to see the classic EB estimates alongside.
  ebUseTiebreak: boolean;
  turf: LoadableState<TurfData>;
  // null = use every item in the question. Sub-key stays trivially
  // serialisable so persistence / rehydration is a no-op.
  turfItemsPool: number[] | null;
  turfComboSize: number;
  // View controls (chart-only; don't trigger a refetch).
  turfMetric: 'reach' | 'frequency';
  turfSortOrder: 'desc' | 'asc';
  turfRender: 'chart' | 'table';
};

const emptyLoadable = <T>(): LoadableState<T> => ({
  loading: false,
  data: null,
  error: null,
});

const initialState: MaxDiffAnalysisState = {
  activeTab: 'counts',
  probeQuestions: {
    loading: false,
    error: null,
    items: [],
    forQuestionId: null,
    requestId: null,
  },
  counts: emptyLoadable<CountsData>(),
  countsView: 'best',
  countsRender: 'chart',
  countsUseTiebreak: true,
  eb: emptyLoadable<EBData>(),
  // Default to Probability-scaled — matches the SurveyMonkey reference
  // default and is the most immediately useful scale for analysts
  // ("which item is most likely to be picked?").
  ebView: 'probability',
  ebRender: 'chart',
  ebSortOrder: 'desc',
  ebShowCi: false,
  ebUseTiebreak: true,
  turf: emptyLoadable<TurfData>(),
  turfItemsPool: null,      // null = all items; component reads pool from Counts data
  turfComboSize: 3,
  turfMetric: 'reach',
  turfSortOrder: 'desc',
  turfRender: 'chart',
};

const maxdiffAnalysisSlice = createSlice({
  name: 'maxdiffAnalysis',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<MaxDiffAnalysisState['activeTab']>) {
      state.activeTab = action.payload;
    },
    setCountsView(state, action: PayloadAction<CountsViewMode>) {
      state.countsView = action.payload;
    },
    setCountsRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.countsRender = action.payload;
    },
    setCountsUseTiebreak(state, action: PayloadAction<boolean>) {
      state.countsUseTiebreak = action.payload;
    },
    setEbView(state, action: PayloadAction<EBViewMode>) {
      state.ebView = action.payload;
    },
    setEbRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.ebRender = action.payload;
    },
    setEbSortOrder(state, action: PayloadAction<'desc' | 'asc'>) {
      state.ebSortOrder = action.payload;
    },
    setEbShowCi(state, action: PayloadAction<boolean>) {
      state.ebShowCi = action.payload;
    },
    setEbUseTiebreak(state, action: PayloadAction<boolean>) {
      state.ebUseTiebreak = action.payload;
    },
    setTurfItemsPool(state, action: PayloadAction<number[] | null>) {
      // null / [] → reset to "use all items"; deduped so repeated clicks
      // on the same item in a multi-select don't inflate the array.
      const v = action.payload;
      state.turfItemsPool = v && v.length > 0 ? Array.from(new Set(v)) : null;
    },
    setTurfComboSize(state, action: PayloadAction<number>) {
      state.turfComboSize = Math.max(1, Math.min(10, action.payload));
    },
    setTurfMetric(state, action: PayloadAction<'reach' | 'frequency'>) {
      state.turfMetric = action.payload;
    },
    setTurfSortOrder(state, action: PayloadAction<'desc' | 'asc'>) {
      state.turfSortOrder = action.payload;
    },
    setTurfRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.turfRender = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaxDiffCounts.pending, (state) => {
        state.counts.loading = true;
        state.counts.error = null;
      })
      .addCase(fetchMaxDiffCounts.fulfilled, (state, action) => {
        state.counts.loading = false;
        state.counts.data = action.payload;
      })
      .addCase(fetchMaxDiffCounts.rejected, (state, action) => {
        state.counts.loading = false;
        state.counts.error =
          (action.error?.message as string) || 'Failed to load counts';
      })
      .addCase(fetchMaxDiffEmpiricalBayes.pending, (state) => {
        state.eb.loading = true;
        state.eb.error = null;
      })
      .addCase(fetchMaxDiffEmpiricalBayes.fulfilled, (state, action) => {
        state.eb.loading = false;
        state.eb.data = action.payload;
      })
      .addCase(fetchMaxDiffEmpiricalBayes.rejected, (state, action) => {
        state.eb.loading = false;
        state.eb.error =
          (action.error?.message as string) ||
          'Failed to load Empirical Bayes results';
      })
      .addCase(fetchMaxDiffTurf.pending, (state) => {
        state.turf.loading = true;
        state.turf.error = null;
      })
      .addCase(fetchMaxDiffTurf.fulfilled, (state, action) => {
        state.turf.loading = false;
        state.turf.data = action.payload;
      })
      .addCase(fetchMaxDiffTurf.rejected, (state, action) => {
        state.turf.loading = false;
        state.turf.error =
          (action.error?.message as string) ||
          'Failed to load TURF results';
      })
      .addCase(fetchMaxDiffProbeQuestions.pending, (state, action) => {
        state.probeQuestions.loading = true;
        state.probeQuestions.error = null;
        state.probeQuestions.requestId = action.meta.requestId;
      })
      .addCase(fetchMaxDiffProbeQuestions.fulfilled, (state, action) => {
        // A superseded request resolving late: leave the newer roster alone.
        if (action.meta.requestId !== state.probeQuestions.requestId) return;
        state.probeQuestions.loading = false;
        state.probeQuestions.items = action.payload;
        state.probeQuestions.forQuestionId = Number(action.meta.arg.questionId);
      })
      .addCase(fetchMaxDiffProbeQuestions.rejected, (state, action) => {
        if (action.meta.requestId !== state.probeQuestions.requestId) return;
        state.probeQuestions.loading = false;
        state.probeQuestions.error =
          (action.error?.message as string) ||
          'Failed to load probe questions';
        // A failed reload must not leave the previous question's items in the
        // tab bar, so the roster is emptied and pinned to the question that
        // failed rather than kept from whichever question loaded last.
        state.probeQuestions.items = [];
        state.probeQuestions.forQuestionId = Number(action.meta.arg.questionId);
      });
  },
});

export const MaxDiffAnalysisSlice = maxdiffAnalysisSlice;
export const {
  setActiveTab,
  setCountsView,
  setCountsRender,
  setCountsUseTiebreak,
  setEbView,
  setEbRender,
  setEbSortOrder,
  setEbShowCi,
  setEbUseTiebreak,
  setTurfItemsPool,
  setTurfComboSize,
  setTurfMetric,
  setTurfSortOrder,
  setTurfRender,
} = maxdiffAnalysisSlice.actions;

export default maxdiffAnalysisSlice.reducer;
